import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./index.module.less";
import { uploadFile } from "@/services/judge";
import { ButtonField, Iconsvg } from "@wk/components-react16";

const ImageCropper = forwardRef(
  ({ changeUserHeaderPic, setIsCropPicModalOpen, messageOpen }, ref) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 200, height: 200 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [resizeHandle, setResizeHandle] = useState("");
    const [scale, setScale] = useState(1);
    const [imgFile, setImgFile] = useState(null);

    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      handleFileSelect,
    }));

    // 处理鼠标滚轮缩放
    const handleWheel = useCallback(
      (e) => {
        // e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newScale = Math.max(0.5, Math.min(3, scale + delta));

        if (newScale !== scale) {
          // 计算缩放中心点（鼠标位置）
          const rect = containerRef.current.getBoundingClientRect();
          const centerX = e.clientX - rect.left;
          const centerY = e.clientY - rect.top;

          // 计算缩放比例
          const scaleRatio = newScale / scale;

          // 调整裁剪区域位置以保持相对于鼠标位置的缩放
          const newCropX = centerX - (centerX - crop.x) * scaleRatio;
          const newCropY = centerY - (centerY - crop.y) * scaleRatio;
          const newCropWidth = crop.width * scaleRatio;
          const newCropHeight = crop.height * scaleRatio;

          // 确保裁剪区域不超出边界
          const containerWidth = 290 * newScale;
          const containerHeight = 300 * newScale;

          setCrop({
            x: Math.max(0, Math.min(containerWidth - newCropWidth, newCropX)),
            y: Math.max(0, Math.min(containerHeight - newCropHeight, newCropY)),
            width: Math.max(100, newCropWidth),
            height: Math.max(100, newCropHeight),
          });

          setScale(newScale);
        }
      },
      [scale, crop]
    );

    // 处理文件选择
    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      setImgFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
      };
      reader.readAsDataURL(file);
    };

    // 图片加载完成后设置初始裁剪区域
    const handleImageLoad = () => {
      if (imageRef.current) {
        const image = imageRef.current;
        const containerWidth = 290;
        const containerHeight = 300;

        setImageSize({ width: containerWidth, height: containerHeight });

        // 计算object-fit: contain下的实际显示尺寸和偏移
        const imageAspectRatio = image.naturalWidth / image.naturalHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let actualDisplayWidth, actualDisplayHeight, offsetX, offsetY;

        if (imageAspectRatio > containerAspectRatio) {
          // 图片更宽，以宽度为准
          actualDisplayWidth = containerWidth * scale;
          actualDisplayHeight = (containerWidth / imageAspectRatio) * scale;
          offsetX = (containerWidth - actualDisplayWidth) / 2;
          offsetY = (containerHeight - actualDisplayHeight) / 2;
        } else {
          // 图片更高，以高度为准
          actualDisplayHeight = containerHeight * scale;
          actualDisplayWidth = containerHeight * imageAspectRatio * scale;
          offsetX = (containerWidth - actualDisplayWidth) / 2;
          offsetY = (containerHeight - actualDisplayHeight) / 2;
        }

        // 设置初始裁剪区域在实际图片显示区域的中心
        const cropSize = Math.min(
          200,
          actualDisplayWidth * 0.8,
          actualDisplayHeight * 0.8
        );
        setCrop({
          x: offsetX + (actualDisplayWidth - cropSize) / 2,
          y: offsetY + (actualDisplayHeight - cropSize) / 2,
          width: cropSize,
          height: cropSize,
        });
      }
    };

    // 开始拖拽
    const handleMouseDown = useCallback(
      (e, type, handle = "") => {
        e.preventDefault();
        e.stopPropagation();
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (type === "drag") {
          setDragStart({ x: x - crop.x, y: y - crop.y });
          setIsDragging(true);
        } else if (type === "resize") {
          setDragStart({ x, y });
          setIsResizing(true);
          setResizeHandle(handle);
        }
      },
      [crop]
    );

    // 处理鼠标移动
    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging && !isResizing) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (isDragging) {
          // 拖拽移动
          const newX = Math.max(
            0,
            Math.min(x - dragStart.x, imageSize.width - crop.width)
          );
          const newY = Math.max(
            0,
            Math.min(y - dragStart.y, imageSize.height - crop.height)
          );
          setCrop((prev) => ({ ...prev, x: newX, y: newY }));
        } else if (isResizing) {
          // 调整大小
          const deltaX = x - dragStart.x;
          const deltaY = y - dragStart.y;

          setCrop((prev) => {
            let newCrop = { ...prev };

            switch (resizeHandle) {
              case "se": // 右下角
                newCrop.width = Math.max(
                  100,
                  Math.min(prev.width + deltaX, imageSize.width - prev.x)
                );
                newCrop.height = Math.max(
                  100,
                  Math.min(prev.height + deltaY, imageSize.height - prev.y)
                );
                break;
              case "sw": // 左下角
                const newWidth = Math.max(100, prev.width - deltaX);
                const newX = Math.max(0, prev.x + prev.width - newWidth);
                newCrop.x = newX;
                newCrop.width = newWidth;
                newCrop.height = Math.max(
                  100,
                  Math.min(prev.height + deltaY, imageSize.height - prev.y)
                );
                break;
              case "ne": // 右上角
                const newHeight = Math.max(100, prev.height - deltaY);
                const newY = Math.max(0, prev.y + prev.height - newHeight);
                newCrop.y = newY;
                newCrop.height = newHeight;
                newCrop.width = Math.max(
                  100,
                  Math.min(prev.width + deltaX, imageSize.width - prev.x)
                );
                break;
              case "nw": // 左上角
                const newW = Math.max(100, prev.width - deltaX);
                const newH = Math.max(100, prev.height - deltaY);
                newCrop.x = Math.max(0, prev.x + prev.width - newW);
                newCrop.y = Math.max(0, prev.y + prev.height - newH);
                newCrop.width = newW;
                newCrop.height = newH;
                break;
            }

            return newCrop;
          });

          setDragStart({ x, y });
        }
      },
      [isDragging, isResizing, dragStart, crop, imageSize, resizeHandle]
    );

    // 结束拖拽
    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle("");
    }, []);

    // 绑定全局鼠标事件
    React.useEffect(() => {
      if (isDragging || isResizing) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

    // 生成裁剪结果
    const generateCroppedImage = () => {
      if (!imageRef.current || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const image = imageRef.current;

      // 计算object-fit: contain下的实际显示尺寸（考虑缩放）
      const containerWidth = 290;
      const containerHeight = 300;
      const imageAspectRatio = image.naturalWidth / image.naturalHeight;
      const containerAspectRatio = containerWidth / containerHeight;

      let actualDisplayWidth, actualDisplayHeight, offsetX, offsetY;

      if (imageAspectRatio > containerAspectRatio) {
        // 图片更宽，以宽度为准
        actualDisplayWidth = containerWidth * scale;
        actualDisplayHeight = (containerWidth / imageAspectRatio) * scale;
        offsetX = (containerWidth - actualDisplayWidth) / 2;
        offsetY = (containerHeight - actualDisplayHeight) / 2;
      } else {
        // 图片更高，以高度为准
        actualDisplayHeight = containerHeight * scale;
        actualDisplayWidth = containerHeight * imageAspectRatio * scale;
        offsetX = (containerWidth - actualDisplayWidth) / 2;
        offsetY = (containerHeight - actualDisplayHeight) / 2;
      }

      // 计算裁剪区域相对于实际图片显示区域的坐标
      const cropX = crop.x - offsetX;
      const cropY = crop.y - offsetY;

      // 计算比例
      const scaleX = image.naturalWidth / actualDisplayWidth;
      const scaleY = image.naturalHeight / actualDisplayHeight;

      // 设置canvas尺寸
      canvas.width = crop.width * scaleX;
      canvas.height = crop.height * scaleY;

      // 绘制裁剪区域
      ctx.drawImage(
        image,
        cropX * scaleX, // 源图片裁剪起始x
        cropY * scaleY, // 源图片裁剪起始y
        crop.width * scaleX, // 源图片裁剪宽度
        crop.height * scaleY, // 源图片裁剪高度
        0, // 目标canvas起始x
        0, // 目标canvas起始y
        canvas.width, // 目标canvas宽度
        canvas.height // 目标canvas高度
      );

      // 生成blob对象
      canvas.toBlob(async (blob) => {
        // 去掉文件名后缀
        let fileName = imgFile.name.split(".")[0];
        // 生成file对象
        const file = new File([blob], fileName + ".png", { type: "image/png" });
        setIsLoading(true);
        try {
          const res = await uploadFile(file);
          if (res?.success) {
            let url = res.message;
            changeUserHeaderPic(url);
            setIsCropPicModalOpen(false);
          } else {
            messageOpen("上传失败,请重试");
          }
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading(false);
        }
      }, "image/png");
    };

    return (
      <div className={styles["image-cropper"]}>
        {/* 文件选择 */}
        {/* <div className="file-input-container">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="file-input"
        />
      </div> */}

        {/* 图片预览和裁剪区域 */}
        {imageSrc && (
          <div className="preview-container">
            <div
              className="image-container"
              ref={containerRef}
              style={{
                position: "relative",
                display: "inline-block",
                overflow: "hidden",
              }}
              onWheel={handleWheel}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="预览图片"
                onLoad={handleImageLoad}
                style={{
                  width: "290px",
                  height: "300px",
                  display: "block",
                  objectFit: "contain",
                  transform: `scale(${scale})`,
                  transformOrigin: "center center",
                }}
                draggable={false}
              />

              {/* 裁剪区域 */}
              <div
                className="crop-area"
                style={{
                  position: "absolute",
                  left: crop.x,
                  top: crop.y,
                  width: crop.width,
                  height: crop.height,
                  border: "1px dashed #ffffff",
                  backgroundColor: "rgba(0, 123, 255, 0.1)",
                  cursor: "move",
                }}
                onMouseDown={(e) => handleMouseDown(e, "drag")}
              >
                {/* 调整大小的控制点 */}
                <div
                  className="resize-handle nw"
                  style={{
                    position: "absolute",
                    top: -4,
                    left: -4,
                    cursor: "nw-resize",
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "resize", "nw")}
                />
                <div
                  className="resize-handle ne"
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    cursor: "ne-resize",
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "resize", "ne")}
                />
                <div
                  className="resize-handle sw"
                  style={{
                    position: "absolute",
                    bottom: -4,
                    left: -4,
                    cursor: "sw-resize",
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "resize", "sw")}
                />
                <div
                  className="resize-handle se"
                  style={{
                    position: "absolute",
                    bottom: -4,
                    right: -4,
                    cursor: "se-resize",
                  }}
                  onMouseDown={(e) => handleMouseDown(e, "resize", "se")}
                />
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="controls">
              <ButtonField>
                <button
                  type="button"
                  onClick={generateCroppedImage}
                  disabled={isLoading}
                >
                  确定
                  {isLoading && <Iconsvg name="spinner-alt" isSpin></Iconsvg>}
                </button>
              </ButtonField>
            </div>

            {/* 隐藏的canvas用于生成裁剪结果 */}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
        )}
      </div>
    );
  }
);

export default ImageCropper;
